/**
 * nutChucNangDanhSach — CHẠY NÚT CHỨC NĂNG RIÊNG của một danh sách (`listActions` trong data_list_config).
 *
 * Tách khỏi phần giao diện (DataListActions.jsx) để phần quyết định — gọi máy chủ thế nào, lỗi báo ra
 * sao, khi nào nạp lại danh sách — kiểm thử được mà không cần dựng React. File này KHÔNG import gì.
 *
 * Mỗi nút khai trong cấu hình:
 *   { code, label, icon, tooltip, variant, confirm, listActionScript }
 * `listActionScript` đã được parseDataListConfig biên dịch thành async function với ĐÚNG thứ tự tham
 * số `THAM_SO_NUT` bên dưới.
 *
 * ⚠️ TÊN FILE: KHÔNG đặt `dataListActions.js` cạnh `DataListActions.jsx`. macOS/Windows không phân biệt hoa
 * thường, nên `import X from './DataListActions'` khớp nhầm file .js này (Vite thử .js trước .jsx) và build
 * báo "default is not exported". Linux (CI) thì không lỗi — xem datalist-list-actions-test nhóm "trùng tên".
 */

/** Thứ tự tham số của `listActionScript`. PHẢI trùng với FUNCTION_ARGS.listActionScript. */
export const THAM_SO_NUT = ['context', 'goiRoute', 'condition', 'selectedIds', 'selectedItems', 'refresh', 'quickAction', 'history', 'code'];

/**
 * NÚT TUỲ CHỈNH CỦA TỪNG DÒNG (`rowActions` trong data_list_config) — cột "Thao tác" của danh sách + màn hình xem.
 *   { code, label, icon, tooltip, color, variant, confirm, showIn: 'all' | 'list' | 'view', rowActionScript }
 * Thứ tự tham số của `rowActionScript`. PHẢI trùng với FUNCTION_ARGS.rowActionScript.
 *   item     : bản ghi của dòng (ở danh sách có thể chỉ là các cột đang hiện — cần đủ thì đọc lại theo item._id)
 *   refresh  : nạp lại danh sách + bản ghi đang xem
 *   apis, token : gọi API khác (vd đọc chứng từ đầy đủ trước khi mở form sửa)
 */
export const THAM_SO_NUT_DONG = ['context', 'goiRoute', 'item', 'refresh', 'quickAction', 'history', 'code', 'apis', 'token'];

/** Giá trị theo đường dẫn có dấu chấm ('exfields.hoi_thoai.conversation_id'). */
const _layDuongDan = (o, p) => String(p || '').split('.').reduce((x, k) => (x == null ? undefined : x[k]), o);
/** Các trường mà `showIfField` cần (đường dẫn, ngăn bằng "|"). */
export const truongDieuKienNut = (nut) => String((nut && nut.showIfField) || '').split('|').map((x) => x.trim()).filter(Boolean);
/**
 * Nút dòng có hiện với BẢN GHI này không: không khai `showIfField` = luôn hiện; có khai = chỉ khi một trong các trường có giá trị
 * (chuỗi khác rỗng, số, true, mảng không rỗng). Vd nút "Mở hội thoại" chỉ có ở lead / cơ hội tạo từ khung chat.
 */
export const nutDongHopLeVoi = (nut, item) => {
  const ds = truongDieuKienNut(nut);
  if (!ds.length) return true;
  return ds.some((p) => {
    const v = _layDuongDan(item, p);
    if (Array.isArray(v)) return v.length > 0;
    return v !== undefined && v !== null && v !== '' && v !== false;
  });
};
/** Gốc của các trường điều kiện (vd 'exfields') — danh sách phải đọc thêm các cột này dù không khai trong `fields`. */
export const truongPhaiDoc = (actions) => [...new Set((Array.isArray(actions) ? actions : []).flatMap(truongDieuKienNut).map((p) => p.split('.')[0]))];

/** Nút dòng có hiện ở chỗ này không: 'list' (cột Thao tác) / 'view' (màn hình xem). Không khai showIn = cả hai. */
export const nutDongHienO = (nut, noi) => {
  const o = String((nut && nut.showIn) || 'all').toLowerCase();
  return o === 'all' || o === noi;
};

/**
 * Biến mọi kiểu lỗi thành một câu đọc được.
 *
 * Lỗi đi qua lớp API có đủ hình dạng: Error, chuỗi, `{ error }`, `{ message }`, phản hồi máy chủ. Hiện
 * thẳng object ra màn hình là "[object Object]" — người dùng thấy có lỗi nhưng không biết lỗi gì.
 */
export const docLoi = (e) => {
  if (e == null) return 'lỗi không rõ';
  if (typeof e === 'string') return e;
  if (e.message) return String(e.message);
  if (e.error) return typeof e.error === 'string' ? e.error : docLoi(e.error);
  if (e.data && e.data.message) return String(e.data.message);
  try { return JSON.stringify(e); } catch (_) { return String(e); }
};

/**
 * `goiRoute(sub, body)` — gọi route riêng của CHÍNH danh sách này: POST `/<apiCode>/<sub>`.
 *
 * VÌ SAO CÓ: mọi nút loại này đều làm đúng một việc — gọi một route của máy chủ. Để mỗi script tự
 * dựng URL, tự gắn token, tự phân tích phản hồi thì mỗi script có một cách sai riêng; lỗi hay gặp
 * nhất là QUÊN kiểm `success === false`, và nút báo "xong" cho một lệnh máy chủ đã từ chối.
 */
export const taoGoiRoute = ({ apis, apiCode, idApp, token }) => async (sub, body) => {
  if (!apis || typeof apis.listBaseUrl !== 'function' || typeof apis.asyncPost !== 'function') {
    throw new Error('Màn hình chưa nhận được context.apis — hãy tải lại trang.');
  }
  if (!sub) throw new Error('goiRoute cần tên route (vd "dong-bo-bai-viet").');
  const url = apis.listBaseUrl(apiCode, idApp, sub) + '?access_token=' + encodeURIComponent(token || '');
  const raw = await apis.asyncPost(url, null, body || {}, 'POST', 'POST', false);
  let rs = raw;
  if (typeof raw === 'string') {
    try { rs = JSON.parse(raw); } catch (e) { rs = { message: raw }; }
  }
  if (rs && rs.success === false) throw new Error(docLoi(rs));
  return rs;
};

/**
 * Chạy một nút. KHÔNG BAO GIỜ ném — luôn trả `{ ok, message, color, refresh }` để giao diện biết báo
 * gì và có nạp lại danh sách hay không.
 *
 * Kết quả script được hiểu như sau:
 *   - trả chuỗi                      -> thành công, báo đúng chuỗi đó
 *   - trả object (vd phản hồi route) -> dùng `message`, `color`, `refresh`; `ok === false` / `success === false` là thất bại
 *   - không trả gì                   -> thành công, không báo, nạp lại danh sách
 *   - ném lỗi                        -> thất bại, báo câu lỗi THẬT, KHÔNG nạp lại
 */
export const chayNutChucNang = (nut, thamSo) => _chay(nut, nut && nut.listActionScript, THAM_SO_NUT, thamSo);

/** Chạy một NÚT DÒNG — cùng hợp đồng kết quả với nút chức năng của danh sách. */
export const chayNutDong = (nut, thamSo) => _chay(nut, nut && nut.rowActionScript, THAM_SO_NUT_DONG, thamSo);

const _chay = async (nut, fn, thuTu, thamSo) => {
  const ten = (nut && (nut.label || nut.code)) || 'Nút chức năng';
  if (typeof fn !== 'function') {
    // Script biên dịch hỏng thì parseDataListConfig đã ghi console và trả undefined. Bấm vào mà im
    // lặng không làm gì là thứ tệ nhất một cái nút có thể làm.
    return { ok: false, color: 'red', refresh: false,
      message: `Nút "${ten}" chưa có script chạy được (script trống hoặc lỗi cú pháp — xem console của trình duyệt).` };
  }
  const t = thamSo || {};
  try {
    const kq = await fn(...thuTu.map((k) => t[k]));
    if (typeof kq === 'string') return { ok: true, message: kq, color: 'green', refresh: true };
    if (kq && typeof kq === 'object') {
      const hong = kq.ok === false || kq.success === false;
      return {
        ok: !hong,
        message: kq.message ? String(kq.message) : '',
        color: kq.color || (hong ? 'red' : 'green'),
        refresh: kq.refresh !== undefined ? kq.refresh !== false : !hong,
      };
    }
    return { ok: true, message: '', color: 'green', refresh: true };
  } catch (e) {
    return { ok: false, message: `${ten}: ${docLoi(e)}`, color: 'red', refresh: false };
  }
};
